import { useState, useEffect } from "react";
import {
  getEntryRequests,
  manageEntryRequest,
  getManagedUsers,
} from "../../services/TimeTracking/api";
import { AppUser } from "../../services/TimeTracking/types";
import { patchEntryRequestAction } from "../../services/TimeTracking/api"; 

export type TimeEntryStatus = "Pending" | "Approved" | "Denied";

export type TimeEntryRequest = {
  id: number;
  userName: string;
  type: "ClockIn" | "ClockOut" | "BreakIn" | "BreakOut";
  time: string;
  comment?: string;
  status: TimeEntryStatus;
};

export type GroupBy = "User" | "Time";

export default function useRequestApprovals() {
  const [allRequests, setAllRequests] = useState<TimeEntryRequest[]>([]);
  const [groupedEntries, setGroupedEntries] = useState<
    { group: string; entries: TimeEntryRequest[] }[]
  >([]);
  const [groupBy, setGroupBy] = useState<GroupBy>("User");
  const [requests, setRequests] = useState<TimeEntryRequest[]>([]);
  const [filteredStatus, setFilteredStatus] = useState<TimeEntryStatus>("Pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [managees, setManagees] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorNotification, setErrorNotification] = useState<string | null>(null);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const entries: any = await getEntryRequests(0, 100);
  
      const formatted: TimeEntryRequest[] = entries.map((entry: any) => {
        const status: TimeEntryStatus = entry.isCanceled
          ? "Denied"
          : entry.isApproved
          ? "Approved"
          : "Pending";
  
        return {
          id: entry.id,
          userName: `${entry.newEntry.user.firstName} ${entry.newEntry.user.lastName}`,
          type: entry.newEntry.type,
          time: entry.newEntry.time,
          comment: entry.comment || "",
          photo: entry.newEntry.user.photo,
          status,
        };
      });
  
      setRequests(formatted);
      setAllRequests(formatted);
      groupEntries(formatted, groupBy);
    } catch (err) {
      console.error("❌ Failed to load entry requests", err);
    } finally {
      setLoading(false);
    }
  };

  const groupEntries = (entries: TimeEntryRequest[], by: GroupBy) => {
    const grouped: Record<string, TimeEntryRequest[]> = {};
    entries.forEach((entry: any) => {
      const key =
        by === "User"
          ? `${entry.userName}`
          : new Date(entry.time).toDateString();

      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(entry);
    });

    const groupedArray = Object.entries(grouped).map(([group, entries]) => ({
      group,
      entries,
    }));
    setGroupedEntries(groupedArray);
  };

  const handleError = (message: string, error: any) => {
    console.error(message, error);
    setErrorNotification(message);
  };
  
  useEffect(() => {
    groupEntries(allRequests, groupBy);
  }, [groupBy]);

  useEffect(() => {
    loadRequests();
    getManagedUsers().then(setManagees);
  }, []);

  const handleApprove = async (id: number) => {
    try {
      await patchEntryRequestAction(id, "approve");
      console.log(`✅ Approved request #${id}`);
      setRequests((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: "Approved" } : r))
      );
      setErrorNotification(`✅ Request #${id} approved successfully.`);
    } catch (err) {
      handleError(`❌ Failed to approve request #${id}`, err);
    }
  };
  
  const handleDeny = async (id: number) => {
    try {
      await patchEntryRequestAction(id, "deny");
      console.log(`❌ Denied request #${id}`);
      setRequests((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: "Denied" } : r))
      );
      setErrorNotification(`❌ Request #${id} denied.`);
    } catch (err) {
      handleError(`❌ Failed to deny request #${id}`, err);
    }
  };



  return {
    handleApprove,
    handleDeny,
    managees, 
    groupedEntries, 
    loading, 
    groupBy, 
    setGroupBy,
    setFilteredStatus,
    filteredStatus,
    setSearchTerm,
    searchTerm,
    errorNotification,
    dismissNotification: () => setErrorNotification(null),
    reloadRequests: loadRequests,
  };
}